import React, { useState } from "react";
import { Check, Star, ShieldCheck, PhoneCall } from "lucide-react";
import { PRICING, LINKS, FINAL } from "../mock";
import { useReveal } from "../hooks/useReveal";

function PlanCard({ plan, popular }) {
  const ref = useReveal();
  const [sel, setSel] = useState(0);
  const opt = plan.options[sel];
  return (
    <div
      ref={ref}
      className={`reveal glass card-lift rounded-3xl p-8 relative ${
        popular ? "ring-1 ring-[#f6e3a8]/50" : ""
      }`}
    >
      {popular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 text-[11px] font-semibold px-3 py-1 rounded-full btn-gold">
          <Star className="w-3 h-3 fill-[#10192c]" /> POPULAIRE
        </span>
      )}
      <p className="eyebrow text-[#7fd8e8] text-[11px]">{plan.label}</p>
      <h3 className="text-2xl md:text-3xl text-white mt-2">{plan.name}</h3>

      <div className="mt-5 flex items-end gap-3">
        <div>
          <span className="text-white/40 line-through text-lg">{plan.original}€</span>
          <div className="text-4xl md:text-5xl font-semibold text-gold leading-none">
            {opt.price}€
          </div>
        </div>
        <span className="mb-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#e0464b]/20 text-[#ff8a8f]">
          -{opt.discount}%
        </span>
      </div>
      {opt.perMonth && (
        <p className="mt-1 text-sm text-white/60">soit {opt.perMonth}€ / mois</p>
      )}

      <div className="mt-5">
        <p className="text-[11px] uppercase tracking-widest text-white/40 mb-2">Options de paiement</p>
        <div className="flex flex-wrap gap-2">
          {plan.options.map((o, i) => (
            <button
              key={o.label}
              onClick={() => setSel(i)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                i === sel
                  ? "btn-gold"
                  : "bg-white/5 text-white/70 hover:bg-white/10 border border-white/10"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-white/50">{opt.sub}</p>
      </div>

      <ul className="mt-6 space-y-3">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-white/80 text-sm">
            <Check className="w-4 h-4 text-[#7fd8e8] mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <a
        href={LINKS.inscription}
        target="_blank"
        rel="noopener noreferrer"
        className={`mt-8 block text-center rounded-full px-6 py-3.5 font-semibold ${
          popular ? "btn-gold" : "btn-ghost"
        }`}
      >
        {popular ? "Je choisis le cursus VIP" : "Je m'inscris"}
      </a>
    </div>
  );
}

export default function Pricing() {
  const head = useReveal();
  const rdv = useReveal();
  const guar = useReveal();
  return (
    <section id="tarifs" className="relative py-24 md:py-28">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div ref={head} className="reveal text-center max-w-3xl mx-auto">
          <p className="eyebrow text-[#f6e3a8] text-xs mb-4">⭐ Offre spéciale · Promo 7 jours</p>
          <h2 className="text-4xl md:text-5xl text-white">
            L'investissement pour rejoindre <span className="text-gold">l'École des Maîtres Dauphins</span>
          </h2>
          <p className="mt-4 text-white/60">
            Jusqu'à <span className="text-[#ff8a8f] font-semibold">-30%</span> selon ton mode de paiement
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-6 items-start">
          <PlanCard plan={PRICING.classique} popular={false} />
          <PlanCard plan={PRICING.vip} popular={true} />
        </div>

        {/* RDV Clarté */}
        <div ref={rdv} className="reveal mt-10 glass rounded-2xl p-7 flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <span className="w-12 h-12 rounded-xl bg-[#1f6f8b]/30 flex items-center justify-center ring-1 ring-white/10">
              <PhoneCall className="w-5 h-5 text-[#7fd8e8]" />
            </span>
            <div>
              <p className="text-white font-medium">Tu hésites ? Réserve un RDV Clarté gratuit</p>
              <p className="text-white/55 text-sm">Un appel de 20 minutes avec Celia pour répondre à toutes tes questions.</p>
            </div>
          </div>
          <a href={LINKS.rdvClarte} target="_blank" rel="noopener noreferrer" className="btn-ghost rounded-full px-6 py-3 font-medium whitespace-nowrap">
            Prendre RDV avec Celia
          </a>
        </div>

        {/* Guarantee */}
        <div ref={guar} className="reveal mt-10 glass rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-center gap-8">
          <img src={FINAL.guaranteeImg} alt="Garantie" className="w-32 h-32 object-contain float-soft" loading="lazy" />
          <div>
            <h3 className="text-2xl md:text-3xl text-white flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-[#27ae8f]" />
              0 risque avec la garantie « satisfait·e ou remboursé·e »
            </h3>
            <p className="mt-3 text-white/70 leading-relaxed">
              Tu as <span className="text-gold font-semibold">14 jours</span> pour faire le test et découvrir le module 1, dans la limite de l'ouverture du Module 1. Si cela ne correspond pas à tes attentes : remboursement intégral.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
